import type { HTMLAttributes, ReactNode, Ref } from "react";

export type CarrierCardColumnConfig = {
	id: string;
	title: ReactNode;
	content: ReactNode;
	className?: string;
	shouldShow?: boolean;
	/** Columns marked "details" render inside a single wrapper (see .carrier-card-details) instead of
	 * as flat siblings, so mobile layouts can give the group one continuous divider. Defaults to "header". */
	group?: "header" | "details";
};

export type CarrierCardLayoutProps = {
	wrapperProps?: HTMLAttributes<HTMLDivElement>;
	cardProps?: HTMLAttributes<HTMLDivElement>;
	contentProps?: HTMLAttributes<HTMLDivElement>;
	overlay?: ReactNode;
	leftControl?: ReactNode;
	logo: ReactNode;
	nameSection: ReactNode;
	columns: CarrierCardColumnConfig[];
	footer?: ReactNode;
	afterContent?: ReactNode;
	wrapperRef?: Ref<HTMLDivElement>;
	banner?: ReactNode;
	bannerClassName?: string;
};

const combineClassNames = (
	defaultClassName: string,
	customClassName: string | undefined,
) => [defaultClassName, customClassName].filter(Boolean).join(" ");

export function CarrierCardLayout({
	banner,
	bannerClassName,
	wrapperProps,
	cardProps,
	contentProps,
	overlay,
	leftControl,
	logo,
	nameSection,
	columns,
	footer,
	afterContent,
	wrapperRef,
}: Readonly<CarrierCardLayoutProps>) {
	const { className: wrapperClassName, ...restWrapperProps } =
		wrapperProps ?? {};
	const { className: cardClassName, ...restCardProps } = cardProps ?? {};
	const { className: contentClassName, ...restContentProps } =
		contentProps ?? {};
	const headerColumns = columns.filter((column) => column.group !== "details");
	const detailColumns = columns.filter((column) => column.group === "details");

	return (
		<div
			ref={wrapperRef}
			{...restWrapperProps}
			className={combineClassNames("carrier-card-wrapper", wrapperClassName)}
		>
			{overlay}
			<div
				{...restCardProps}
				className={combineClassNames("carrier-card", cardClassName)}
			>
				{banner ? (
					<div
						className={combineClassNames(
							"carrier-card-banner",
							bannerClassName,
						)}
					>
						{banner}
					</div>
				) : null}

				<div
					{...restContentProps}
					className={combineClassNames(
						"carrier-card-content",
						contentClassName,
					)}
				>
					<div className={"carrier-card-header"}>
						{leftControl}
						{logo}
						{nameSection}
						{headerColumns.map((column) => (
							<div
								key={column.id}
								className={combineClassNames(
									"carrier-card-col",
									column.className,
								)}
								data-testid={`carrier-card-col-${column.id}`}
							>
								<div className={"carrier-card-col-title"}>{column.title}</div>
								{column.content}
							</div>
						))}
					</div>
					{detailColumns.length > 0 ? (
						<div className={"carrier-card-details"}>
							{detailColumns.map((column) => (
								<div
									key={column.id}
									className={combineClassNames(
										"carrier-card-col",
										column.className,
									)}
									data-testid={`carrier-card-col-${column.id}`}
								>
									<div className={"carrier-card-col-title"}>{column.title}</div>
									{column.content}
								</div>
							))}
						</div>
					) : null}
					{footer}
				</div>
				{afterContent}
			</div>
		</div>
	);
}
